import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Heart, Wine, Music2, Radio, Megaphone } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_HERO = 'https://static.prod-images.emergentagent.com/jobs/85590517-1907-43ea-b1e2-986cb80bbd71/images/0d72697b65c5ce1db04a7f269664c0fb0dcb95cda83deea22f05a87f78d139dc.jpeg';

const COLUMNS = [
  {
    key: 'listeners',
    title: 'Listeners & Supporters',
    eyebrow: 'For the people who tune in',
    Icon: Radio,
    blurb: 'You are the reason the red light goes on. Every episode of Wine Talks with Paul K and The Song of America is researched, recorded, edited and published independently — no network, no paywall. If the conversations have poured something good into your week, a contribution at any level helps cover studio time, editing and hosting, and keeps every episode free for everyone.',
  },
  {
    key: 'guests',
    title: 'Guests & PR Firms',
    eyebrow: 'For past guests, publicists & PR teams',
    Icon: Megaphone,
    blurb: 'Your stories deserve a stage that sounds as good as they do. For past guests, publicists and PR teams, backing the shows is a professional investment: it underwrites the production and promotion that carry your episode — and your clients — to a growing audience. Never required, never tied to booking decisions, always deeply appreciated.',
  },
];

export default function SupportPage({ audience }) {
  const [params] = useSearchParams();
  const [tiers, setTiers] = useState(null);
  const [heroImage, setHeroImage] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [busyTier, setBusyTier] = useState(null);

  useEffect(() => {
    axios.get(`${API}/support/config`).then(r => {
      setTiers(r.data.tiers || []);
      setHeroImage(r.data.heroImage || '');
    });
    if (params.get('canceled')) toast({ title: 'No worries', description: 'Your contribution was canceled — the shows go on!' });
  }, []); // eslint-disable-line

  const contribute = async (tier) => {
    setBusyTier(tier.id);
    try {
      const r = await axios.post(`${API}/support/checkout`, {
        tierId: tier.id,
        origin_url: window.location.origin,
        donorName: donorName || undefined,
        donorEmail: donorEmail || undefined,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast({ title: 'Could not start checkout', description: e?.response?.data?.detail || String(e) });
      setBusyTier(null);
    }
  };

  const columns = audience ? COLUMNS.filter(c => c.key === audience) : COLUMNS;
  const heroSubtitle = audience === 'guests'
    ? 'For our guests, publicists and PR teams — an optional way to invest in your appearance and walk away with real, professionally produced marketing assets.'
    : audience === 'listeners'
      ? 'Independent conversations, poured with care. If the shows have poured something good into your week, help keep the mics on — entirely optional, deeply appreciated.'
      : 'Independent conversations, poured with care. If you like what you hear, help keep the mics on — entirely optional, deeply appreciated.';

  return (
    <div className="min-h-screen bg-[#1a1214] text-stone-100" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Hero */}
      <div className="relative h-[420px] sm:h-[480px] overflow-hidden">
        <img src={heroImage || DEFAULT_HERO} alt="Podcast studio with microphone, guitar and a glass of wine" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(26,18,20,0.35) 0%, rgba(26,18,20,0.55) 55%, #1a1214 100%)' }} />
        <div className="relative max-w-5xl mx-auto px-6 h-full flex flex-col justify-end pb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-10 w-10 rounded-md grid place-items-center" style={{ background: '#722F37' }}><Wine className="w-5 h-5" /></span>
            <span className="h-10 w-10 rounded-md grid place-items-center" style={{ background: '#1B2D5B' }}><Music2 className="w-5 h-5" /></span>
            <span className="text-[12px] tracking-[0.25em] uppercase text-stone-300">Wine Talks with Paul K · The Song of America</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl" data-testid="support-page-title">Support the Shows</h1>
          <p className="text-stone-300 text-base max-w-2xl mt-3 leading-relaxed">{heroSubtitle}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-16">
        {tiers === null && <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-stone-500" /></div>}
        {tiers?.length === 0 && <div className="text-stone-400 py-10">Contribution levels are being set up — check back soon.</div>}
        {tiers?.length > 0 && (
          <div className={`grid ${audience ? 'max-w-2xl mx-auto' : 'lg:grid-cols-2'} gap-10 items-start mt-12`}>
            {columns.map(col => (
              <div key={col.key} data-testid={`support-column-${col.key}`}>
                {/* Explainer block */}
                <div className="mb-6">
                  <div className="rounded-lg border border-[#C9A961]/40 px-5 py-4 mb-4 flex items-center gap-4" style={{ background: 'linear-gradient(90deg, rgba(201,169,97,0.16) 0%, rgba(201,169,97,0.03) 100%)' }} data-testid={`support-column-header-${col.key}`}>
                    <span className="h-12 w-12 rounded-full grid place-items-center shrink-0" style={{ background: '#C9A961' }}>
                      <col.Icon className="w-5 h-5 text-[#1a1214]" />
                    </span>
                    <div>
                      <div className="text-[11px] tracking-[0.22em] uppercase text-stone-400">{col.eyebrow}</div>
                      <h2 className="text-2xl sm:text-[28px] text-[#C9A961] leading-tight">{col.title}</h2>
                    </div>
                  </div>
                  <p className="text-stone-300 text-[14px] leading-relaxed" data-testid={`support-blurb-${col.key}`}>{col.blurb}</p>
                </div>
                <div className="grid gap-4">
                  {tiers.filter(t => (t.audience || 'listeners') === col.key).map(t => (
                    <div key={t.id} data-testid={`support-tier-${t.id}`} className="rounded-lg border border-stone-700/60 bg-[#241a1c] p-5 hover:border-[#C9A961] transition-colors">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-lg text-[#C9A961]">{t.name}</span>
                        <span className="text-xl font-semibold shrink-0">{Number(t.amount) > 0 ? `$${Number(t.amount).toFixed(0)}${t.interval === 'month' ? '/mo' : ''}` : 'Free'}</span>
                      </div>
                      <p className="text-stone-300 text-[13.5px] mt-2 leading-relaxed">{t.description}</p>
                      <div className="mt-4">
                        {Number(t.amount) > 0 ? (
                          <Button
                            data-testid={`support-contribute-${t.id}`}
                            onClick={() => contribute(t)}
                            disabled={!!busyTier}
                            className="w-full bg-[#722F37] hover:bg-[#8a3a44] text-white py-5 text-[15px]"
                          >
                            {busyTier === t.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
                            {t.interval === 'month' ? 'Contribute monthly' : 'Contribute'}
                          </Button>
                        ) : (
                          <Button
                            asChild
                            data-testid={`support-follow-${t.id}`}
                            variant="outline"
                            className="w-full border-[#C9A961] text-[#C9A961] bg-transparent hover:bg-[#C9A961]/10 py-5 text-[15px]"
                          >
                            <a href="https://www.winetalkspodcast.com" target="_blank" rel="noreferrer">Follow the shows</a>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 rounded-lg border border-stone-700/60 bg-[#241a1c] p-5 max-w-3xl">
          <div className="text-sm text-stone-300 mb-3">Optional — tell us who to thank (initials may appear in our supporter list):</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="support-donor-name" value={donorName} onChange={(e) => setDonorName(e.target.value)} placeholder="Your name (optional)" className="bg-[#1a1214] border-stone-700 text-stone-100 placeholder:text-stone-500" />
            <Input data-testid="support-donor-email" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} placeholder="Email for your receipt (optional)" className="bg-[#1a1214] border-stone-700 text-stone-100 placeholder:text-stone-500" />
          </div>
        </div>

        <p className="text-stone-500 text-xs mt-10">Payments are processed securely by Stripe. Contributions are voluntary and non-refundable gifts in support of the podcasts. Contributions never influence booking or editorial decisions.</p>
      </div>
    </div>
  );
}
